// ─────────────────────────────────────────────────────────────────────────────
// Tree-Field-Optionen: zentrale Dropdown-Werte
//
// Werte stammen aus der alten Baumwächter-App (v1.5.0, Maria 13.05.2026).
// Diese Datei ist die "Source of Truth" für strukturierte Felder.
// ─────────────────────────────────────────────────────────────────────────────

export const ZUSTAND_OPTIONS = [
  'gut',
  'leichte Mängel',
  'erhebliche Mängel',
  'Gefahr im Verzug',
  'abgestorben',
  'gefällt',
];

export const VITALITAET_OPTIONS = [
  '0 – vital',
  '1 – leicht geschwächt',
  '2 – geschwächt',
  '3 – stark geschwächt',
];

export const ENTWICKLUNG_OPTIONS = [
  'Jugendphase',
  'Reifephase',
  'Alterungsphase',
  'Abgangsphase',
];

export const SICHERHEIT_OPTIONS = [
  'hoch',
  'niedrig',
];

export const LEBENSERWARTUNG_OPTIONS = [
  'kurz',
  'mittel',
  'lang',
];

// FEAT-026: Verkehrssicherheit – ersetzt im UI die Lebenserwartung.
// Vier Werte (inkl. „Zweifelhaft" aus den Wanzleben-Importdaten).
export const VERKEHRSSICHERHEIT_OPTIONS = [
  'Gegeben',
  'Zweifelhaft',
  'Nicht gegeben',
  'Wiederherstellbar',
];

// Farb-Mapping für die Anzeige: grün = gegeben, gelb/amber = zweifelhaft &
// wiederherstellbar, rot = nicht gegeben.
export function getVerkehrssicherheitColor(value?: string | null): { color: string; bg: string } {
  switch (value) {
    case 'Gegeben':           return { color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' };
    case 'Zweifelhaft':       return { color: '#b5923a', bg: 'rgba(212,168,67,0.18)' };
    case 'Wiederherstellbar': return { color: '#b5923a', bg: 'rgba(212,168,67,0.18)' };
    case 'Nicht gegeben':     return { color: '#d32f2f', bg: 'rgba(211,47,47,0.12)' };
    default:                  return { color: 'inherit', bg: 'transparent' };
  }
}

export const KONTROLL_INTERVALL_OPTIONS = [
  'halbjährlich',
  'jährlich',
  '2-jährlich',
  '3-jährlich',
];

export const ANZAHL_STAEMME_OPTIONS = [1, 2, 3, 4];

// Funktion (vorher "Grünfläche") – wo der Baum steht (FEAT-017, erw. BUG-022)
// Wird im DB-Feld grunflache gespeichert (Backwards-Compat zur alten App).
// Reihenfolge: alphabetisch + häufige Werte oben.
export const FUNKTION_OPTIONS = [
  'Allee',
  'Einzelbaum',
  'Friedhof',
  'Kindergarten',
  'öffentliche Einrichtung',
  'Park',
  'Schule',
  'Spielplatz',
  'Sportplatz',
  'Straßenbegleitgrün',
  'Sonstiges',
];

// ─────────────────────────────────────────────────────────────────────────────
// Maßnahmentypen nach FLL-Baumkontrollrichtlinie (BUG-018 / BUG-021)
//
// Wie bei Schadmerkmalen: jeder Eintrag hat einen Kurz-Wert (gespeichert) plus
// eine ausführliche FLL-Beschreibung (Hilfetext im Dropdown + Auto-Fill der
// Beschreibung).
//
// MEASURE_TYPE_FLL: Die offizielle FLL-Liste mit Beschreibungen
// MEASURE_TYPE_EXTRA: Eigene Erweiterungen für die Praxis (Kontrolle, Düngung etc.)
// ─────────────────────────────────────────────────────────────────────────────

export interface MeasureType {
  value: string;
  beschreibung: string;
}

export const MEASURE_TYPE_FLL: MeasureType[] = [
  { value: 'Totholzentfernung',                          beschreibung: 'Totholzentfernung' },
  { value: 'eingehende Untersuchung erforderlich',       beschreibung: 'keine Angabe möglich ohne die Ergebnisse der eingehenden Untersuchung' },
  { value: 'Aufbau- und Erziehungsschnitt',              beschreibung: 'Jungbaumpflege (Erziehungs- und Aufbauschnitt)' },
  { value: 'Lichtraumprofilschnitt',                     beschreibung: 'Lichtraumprofilschnitt' },
  { value: 'Kronenpflege',                               beschreibung: 'Kronenpflege' },
  { value: 'Entfernung Stamm- und Stockaustriebe',       beschreibung: 'Entfernung Stamm- und Stockaustriebe' },
  { value: 'Einkürzung von Ästen',                       beschreibung: 'Einkürzung von Ästen: xx-Seite um xx m' },
  { value: 'Einkürzung von Kronenteilen',                beschreibung: 'Einkürzung von Kronenteilen: xx-Seite um xx m' },
  { value: 'Kroneneinkürzung seitlich und in der Höhe',  beschreibung: 'Einkürzung der Krone seitlich und in der Höhe um xx m' },
  { value: 'Kroneneinkürzung in der Höhe',               beschreibung: 'Einkürzung der Krone in der Höhe um xx m' },
  { value: 'Kappung auf Resthöhe',                       beschreibung: 'Kappung auf eine Resthöhe von xx m' },
  { value: 'Kopfbaumschnitt',                            beschreibung: 'Kopfbaumschnitt' },
  { value: 'Entfernung angebrochener und loser Äste',    beschreibung: 'Entfernung angebrochener und loser Äste' },
  { value: 'Entfernung baumfremder Bewuchs',             beschreibung: 'Entfernung baumfremder Bewuchs' },
  { value: 'Einbau einer statischen Kronensicherung',    beschreibung: 'Einbau einer statischen Kronensicherung: x-fach Verbindung, Bruchlast x t' },
  { value: 'Einbau einer dynamischen Kronensicherung',   beschreibung: 'Einbau einer dynamischen Kronensicherung: x-fach Verbindung, Bruchlast x t' },
  { value: 'Rückbau 3-Bock',                             beschreibung: 'Abbau von Baumverankerungen und Stammschutzmaterialien bei Jungbäumen' },
  { value: 'Behandlung von frischen Anfahrschäden',      beschreibung: 'Behandlung von frischen und flächigen Rindenablösungen (z.B. Anfahrschäden)' },
  { value: 'Fällung',                                    beschreibung: 'Fällung' },
];

// Zusätzliche, nicht-FLL Maßnahmen aus der täglichen Praxis
export const MEASURE_TYPE_EXTRA: MeasureType[] = [
  { value: 'Kontrolle',                                   beschreibung: 'Regelkontrolle nach Kontrollintervall' },
  { value: 'Untere Naturschutzbehörde hinzuziehen',       beschreibung: 'Vor Maßnahme: Genehmigung bei Unterer Naturschutzbehörde einholen' },
  { value: 'Rodung',                                      beschreibung: 'Rodung inkl. Wurzelstock' },
  { value: 'Wurzelbehandlung',                            beschreibung: 'Wurzelbehandlung' },
  { value: 'Bewässerung',                                 beschreibung: 'Bewässerung' },
  { value: 'Düngung',                                     beschreibung: 'Düngung' },
  { value: 'Pflanzung',                                   beschreibung: 'Neupflanzung' },
  { value: 'Sonstiges',                                   beschreibung: 'Sonstiges: (Bemerkung)' },
];

// Backwards-Compat: nur die value-Strings
export const MEASURE_TYPE_OPTIONS_FLL   = MEASURE_TYPE_FLL.map(m => m.value);
export const MEASURE_TYPE_OPTIONS_EXTRA = MEASURE_TYPE_EXTRA.map(m => m.value);

// Kombinierte Liste – z.B. für Fuzzy-Match-Validierung beim CSV-Import
export const MEASURE_TYPE_OPTIONS = [
  ...MEASURE_TYPE_OPTIONS_FLL,
  ...MEASURE_TYPE_OPTIONS_EXTRA,
];

// Gruppierte Variante mit Header-Labels – für Dropdowns mit Section-Divider
export const MEASURE_TYPE_GROUPS: { label: string; items: MeasureType[] }[] = [
  { label: 'FLL-Maßnahmen', items: MEASURE_TYPE_FLL },
  { label: 'Weitere',       items: MEASURE_TYPE_EXTRA },
];

// Helper: Beschreibung zu einem Maßnahmen-value finden
export function getMeasureBeschreibung(value: string): string {
  const found = [...MEASURE_TYPE_FLL, ...MEASURE_TYPE_EXTRA].find(m => m.value === value);
  return found?.beschreibung || value;
}

// ─────────────────────────────────────────────────────────────────────────────
// Schadmerkmale nach FLL-Baumkontrollrichtlinie (BUG-018 / BUG-019)
//
// Jedes Merkmal hat:
//   - value:        kurzer Wert (wird gespeichert + für Filter/Auswertung benutzt)
//   - beschreibung: ausführliche FLL-Beschreibung mit Platzhaltern (xx) für Details
//                   wie Höhe, Seite, sondierbare Tiefe – wird beim Auswählen in
//                   das Freitext-Befund-Feld vorausgefüllt
//
// "ohne besonderen Befund" steht jeweils als erstes (Default-Wert).
// ─────────────────────────────────────────────────────────────────────────────

export interface Schadmerkmal {
  value: string;
  beschreibung: string;
}

export const SCHADMERKMAL_KRONE: Schadmerkmal[] = [
  { value: 'ohne besonderen Befund',              beschreibung: 'ohne besonderen Befund' },
  { value: 'Totholz',                             beschreibung: 'Totholz mit mehr als 3 cm Durchmesser' },
  { value: 'Lichtraumprofil nicht gegeben',       beschreibung: 'die Äste ragen ins Lichtraumprofil' },
  { value: 'Fehlentwicklungen',                   beschreibung: 'Fehlentwicklungen (sich kreuzende und reibende Äste, mit dem Leittrieb konkurrierende Seitenäste, Äste mit eingewachsener Rinde)' },
  { value: 'Baum wurde ehemals gekappt',          beschreibung: 'der Baum wurde ehemals gekappt, die Länge der Reiterate beträgt xx m' },
  { value: 'Astungswunde',                        beschreibung: 'Astungswunde in einer Höhe von xx m auf xx-Seite, xx cm sondierbar' },
  { value: 'Astabriss',                           beschreibung: 'Astabriss in einer Höhe von xx m auf xx-Seite' },
  { value: 'Baumfremder Bewuchs',                 beschreibung: 'baumfremder Bewuchs mit xx' },
  { value: 'Absterbeerscheinungen in der Oberkrone', beschreibung: 'Absterbeerscheinungen in der Oberkrone' },
  { value: 'deformierte Krone',                   beschreibung: 'deformierte Krone: (Form, Ausrichtung)' },
  { value: 'Auffälligkeiten in der Belaubung',    beschreibung: 'Auffälligkeiten in der Belaubung:' },
  { value: 'lose Äste',                           beschreibung: 'lose aufliegende Äste' },
  { value: 'angebrochener Ast',                   beschreibung: 'angebrochener Ast' },
  { value: 'Unglücksbalken',                      beschreibung: 'Unglücksbalken' },
  { value: 'Symptome des Eschentriebsterbens',    beschreibung: 'zeigt Symptome des Eschentriebsterbens' },
  { value: 'Druckzwiesel',                        beschreibung: 'Druckzwiesel' },
  { value: 'Pilzbefall',                          beschreibung: 'Pilzbefall mit xx Art, in xx m Höhe, auf xx-Seite' },
  { value: 'Höhlung',                             beschreibung: 'Höhlung in einer Höhe von xx m auf xx-Seite, xx cm sondierbar' },
  { value: 'vorhandene Kronensicherung',          beschreibung: 'vorhandene Kronensicherung: (Typ, x-fach-Verbindung, Alter)' },
  { value: 'Sonstiges',                           beschreibung: 'Sonstiges:' },
];

export const SCHADMERKMAL_STAMM: Schadmerkmal[] = [
  { value: 'ohne besonderen Befund',              beschreibung: 'ohne besonderen Befund' },
  { value: 'Druckzwiesel',                        beschreibung: 'Druckzwiesel in einer Höhe von xx m und mit xx Defekte' },
  { value: 'Vergabelung',                         beschreibung: 'Vergabelung in xx Stämmlinge in xx m Höhe' },
  { value: 'Zugzwiesel',                          beschreibung: 'Zugzwiesel in einer Höhe von xx m' },
  { value: 'Stammaustriebe (Lichtraumprofil)',    beschreibung: 'Stammaustriebe ragen in das Lichtraumprofil des Verkehrsraumes' },
  { value: 'Astungswunde',                        beschreibung: 'Astungswunde in einer Höhe von xx m auf xx-Seite, xx cm sondierbar' },
  { value: 'Pilzbefall',                          beschreibung: 'Pilzbefall in einer Höhe von xx m auf xx-Seite mit xx Art' },
  { value: 'Höhlung',                             beschreibung: 'Höhlung in einer Höhe von xx m auf xx-Seite, xx cm sondierbar' },
  { value: 'Baumfremder Bewuchs',                 beschreibung: 'baumfremder Bewuchs bis in eine Höhe von xx m mit xx Art' },
  { value: 'Schrägstand',                         beschreibung: 'Schrägstand nach xx' },
  { value: 'Rindenschaden',                       beschreibung: 'Rindenschaden in einer Höhe von xx m auf xx-Seite' },
  { value: 'Riss',                                beschreibung: 'Riss in einer Höhe von xx m bis xx m auf xx-Seite' },
  { value: 'Dumpfklang (innere Fäule)',           beschreibung: 'Dumpfklang aufgrund innerer Fäule in einer Höhe von xx m auf xx-Seite' },
  { value: 'Insektenbefall',                      beschreibung: 'Befall mit xx Art' },
  { value: 'Sonstiges',                           beschreibung: 'Sonstiges:' },
];

export const SCHADMERKMAL_STAMMFUSS: Schadmerkmal[] = [
  { value: 'ohne besonderen Befund',              beschreibung: 'ohne besonderen Befund' },
  { value: 'Verdacht auf Stockfäule',             beschreibung: 'Verdacht auf Stockfäule' },
  { value: 'Stammfußverdickung',                  beschreibung: 'Stammfußverdickung' },
  { value: 'Pilzbefall',                          beschreibung: 'Pilzbefall mit xx Art auf xx-Seite' },
  { value: 'Höhlung',                             beschreibung: 'Höhlung auf der xx-Seite, xx cm sondierbar' },
  { value: 'Stockaustriebe',                      beschreibung: 'Stockaustriebe ragen in das Lichtraumprofil des Verkehrsraumes' },
  { value: 'Rindenschaden',                       beschreibung: 'Rindenschaden' },
  { value: 'Vergabelung',                         beschreibung: 'Vergabelung in xx Stämmlinge in xx m Höhe' },
  { value: 'Druckzwiesel',                        beschreibung: 'Druckzwiesel in einer Höhe von xx m und mit xx Defekte' },
  { value: 'Zugzwiesel',                          beschreibung: 'Zugzwiesel in einer Höhe von xx m' },
  { value: 'Wurzelschäden',                       beschreibung: 'Wurzelschäden durch xx' },
  { value: 'Bodenverdichtung',                    beschreibung: 'Bodenverdichtung' },
  { value: 'Bodenversiegelung',                   beschreibung: 'Bodenversiegelung' },
  { value: 'Abgrabung',                           beschreibung: 'Abgrabung' },
  { value: 'Überfüllung',                         beschreibung: 'Überfüllung' },
  { value: 'Bodenrisse',                          beschreibung: 'Bodenrisse auf xx-Seite' },
  { value: 'Sonstiges',                           beschreibung: 'Sonstiges:' },
];

// Helper: alle value-Strings einer Liste – für die alte Multi-Select-API
export const SCHADMERKMAL_KRONE_OPTIONS     = SCHADMERKMAL_KRONE.map(m => m.value);
export const SCHADMERKMAL_STAMM_OPTIONS     = SCHADMERKMAL_STAMM.map(m => m.value);
export const SCHADMERKMAL_STAMMFUSS_OPTIONS = SCHADMERKMAL_STAMMFUSS.map(m => m.value);

// Helper: Beschreibung zu einem value finden (für Vor-Ausfüllen + Tooltip)
export function getMerkmalBeschreibung(bereich: 'krone' | 'stamm' | 'stammfuss', value: string): string {
  const list = bereich === 'krone' ? SCHADMERKMAL_KRONE
             : bereich === 'stamm' ? SCHADMERKMAL_STAMM
             : SCHADMERKMAL_STAMMFUSS;
  return list.find(m => m.value === value)?.beschreibung || value;
}

